import { GridColDef } from "@mui/x-data-grid";
import { useMemo } from "react";
import CustomDataGrid from "../../../components/ui/customDataGrid/customDataGrid.component";
import { useTranslation } from "react-i18next";
import { Print } from "@mui/icons-material";
import { EQUIPMENTS_ENDPOINT, EQUIPMENT_KIND_OPTIONS, EQUIPMENT_OWNERSHIP_OPTIONS } from "../../../consts";
import { useNavigate } from "react-router-dom";
import GridAddressCell from "../../../components/ui/gridAddressCell/gridAddressCell.component";
import { useTenantName } from "../../../contexts/auth";
import {
  containsStringFilterOperators,
  equalsNumberFilterOperators,
  singleSelectFilterOperators,
} from "../../../components/ui/customDataGrid/filterOperators";

export default function EquipmentList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Equipment with no installation address is not out at a client's site - it
  // is back with us, so the workspace's own name stands in for the address.
  const tenantName = useTenantName();

  const OWNERSHIP_OPTIONS = useMemo(
    () => EQUIPMENT_OWNERSHIP_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
    [t]
  );
  const KIND_OPTIONS = useMemo(
    () => EQUIPMENT_KIND_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
    [t]
  );

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      filterOperators: equalsNumberFilterOperators(),
    },
    {
      field: "name",
      headerName: t("Equipment"),
      filterable: false,
      valueGetter: (value, row) => row.full_name,
    },
    {
      field: "client_name",
      headerName: t("Client Name"),
      sortable: false,
      filterOperators: containsStringFilterOperators(),
      valueGetter: (value, row) => row.client_name || "",
    },
    {
      field: "ownership",
      headerName: t("Ownership"),
      editable: false,
      type: "singleSelect",
      valueOptions: OWNERSHIP_OPTIONS,
      filterOperators: singleSelectFilterOperators(),
      valueGetter: (value, row) => row.ownership,
      renderCell: (params) => params.row.ownership_display,
    },
    {
      field: "kind",
      headerName: t("Kind"),
      editable: false,
      type: "singleSelect",
      valueOptions: KIND_OPTIONS,
      filterOperators: singleSelectFilterOperators(),
      valueGetter: (value, row) => row.kind,
      renderCell: (params) => params.row.kind_display,
    },
    {
      field: "is_installed",
      headerName: t("Is installed"),
      type: "boolean",
    },
    {
      field: "installation_address",
      headerName: t("Installation address"),
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <GridAddressCell
            address={params.row.installation_address}
            fallbackText={tenantName}
          />
        );
      },
      valueGetter: (value, row) => {
        const address = row.installation_address;
        if (
          address &&
          address.street &&
          address.city &&
          address.zip_code &&
          address.country
        ) {
          return `${address.street}, ${address.city}, ${address.zip_code}, ${address.country}`;
        }
        return tenantName;
      },
    },
  ];

  const handleRowClick = (id: string) => {
    navigate(`/equipment-section/equipments/${id}`);
  };

  return (
    <CustomDataGrid
      icon={<Print />}
      title={t("Equipment")}
      url={EQUIPMENTS_ENDPOINT}
      gridSettingsKey="EQUIPMENTS"
      columns={columns}
      refreshEventName="equipmentAdded"
      onRowClick={handleRowClick}
    />
  );
}
